import React, { useMemo } from 'react';

import { getVideoCover, isLandscape, isVideo } from '@libs/NwUtils';

import NwIcon from '@components/Gui/NwIcon';
import { isPlaceholder } from '@components/PlaylistEditor/PlaylistEditorUtils';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Una pagina del book. Il tap apre le azioni sulla pagina stessa, la pressione
 * prolungata avvia il drag di dnd-kit: entrambi insistono sullo stesso elemento,
 * per questo il sensore touch e' attivato con ritardo.
 */
const MobilePageCard = ({
    playlistItem,
    showPageNumber,
    isSelected,
    isInHand,
    isPlacing,
    onSelect,
    onMove,
    onPreview,
    onRemove,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: `sortable-${playlistItem.ID}`,
        disabled: isPlacing,
    });

    const placeholder = isPlaceholder(playlistItem);
    const media = playlistItem.media;
    const video = !placeholder && isVideo(media);
    const landscape = !placeholder && isLandscape(media);

    let thumbnailUrl = '';
    if (!placeholder) {
        thumbnailUrl = video ? getVideoCover(media) : media.Thumbnail && media.Thumbnail.Url;
    }

    const pageClasses = useMemo(() => {
        let classes = 'book-page';
        if (landscape) {
            classes += ' landscape';
        }
        if (placeholder) {
            classes += playlistItem.unremovable ? ' blank blank-locked' : ' blank';
        }
        if (isDragging) {
            classes += ' dragging';
        }
        if (isInHand) {
            classes += ' in-hand';
        }
        return classes;
    }, [landscape, placeholder, playlistItem.unremovable, isDragging, isInHand]);

    const pageLabel = placeholder
        ? `Blank page ${playlistItem.page}`
        : `${video ? 'Video' : 'Photo'} on page ${playlistItem.page}`;

    const style = {
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        transition: transition,
    };

    const handleAction = (action) => (event) => {
        event.stopPropagation();
        action(playlistItem);
    };

    return (
        <div ref={setNodeRef} className={pageClasses} style={style}>
            {placeholder ? (
                <span className="blank-mark">
                    <span>{playlistItem.unremovable ? 'Blank, required' : 'Blank page'}</span>
                </span>
            ) : (
                <img src={thumbnailUrl} alt={pageLabel} />
            )}
            {isInHand && <span className="in-hand-mark">In hand</span>}
            {showPageNumber && !placeholder && <span className="page-number">{playlistItem.page}</span>}
            {video && (
                <span className="page-video-mark">
                    <NwIcon icon={byPrefixAndName.fass['play']} />
                </span>
            )}
            <button
                type="button"
                className="page-hit"
                aria-label={pageLabel}
                onClick={handleAction(onSelect)}
                {...attributes}
                {...listeners}
            />
            {isSelected && (
                <div className="page-actions">
                    {!placeholder && (
                        <button type="button" onClick={handleAction(onPreview)}>
                            View
                        </button>
                    )}
                    <button type="button" className="action-move" onClick={handleAction(onMove)}>
                        Move
                    </button>
                    {!playlistItem.unremovable && (
                        <button type="button" className="action-remove" onClick={handleAction(onRemove)}>
                            Remove
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default MobilePageCard;
